import { motion } from "framer-motion";
import { Mail, ArrowUp } from "lucide-react";
import { Button } from "./ui/button";

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToDemo = (e) => {
    e.preventDefault();
    const element = document.querySelector("#book-demo");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const footerLinks = [
    { href: "#problem", label: "The Problem" },
    { href: "#features", label: "How It Works" },
    { href: "#why-us", label: "Why Us" },
    { href: "#testimonials", label: "Results" },
    { href: "#book-demo", label: "Book Demo" },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer
      className="bg-navy text-white relative overflow-hidden"
      data-testid="footer"
    >
      {/* CTA Section */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2
              className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight tracking-tight mb-4"
              data-testid="footer-cta-headline"
            >
              Ready to End the Chasing Culture?
            </h2>
            <p className="font-body text-white/70 mb-8 max-w-xl mx-auto">
              Join CA firms across India who've transformed their document 
              management. Your first demo is on us.
            </p>
            <Button
              onClick={scrollToDemo}
              className="bg-gold text-navy font-body font-semibold tracking-wide uppercase text-sm px-8 py-4 rounded-full transition-all duration-300 hover:bg-gold/90 active:scale-98"
              data-testid="footer-cta-button"
            >
              Schedule My Prototype Demo
            </Button>
          </motion.div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
        <div className="grid md:grid-cols-3 gap-12 items-start">
          {/* Brand */}
          <div>
            <div className="mb-4">
              <span className="font-heading text-2xl font-bold text-white tracking-tight">
                InwardSync
              </span>
              <span className="block font-body text-sm text-white/60 font-medium uppercase tracking-widest mt-1">
                Automations
              </span>
            </div>
            <p className="font-body text-white/60 text-sm leading-relaxed mb-6">
              Process Transformation Services for CA Firms. 
              We turn document chaos into organized peace of mind.
            </p>
            <div className="flex items-center gap-2">
              <Mail size={18} className="text-gold" />
              <a
                href="mailto:ivoryandsaffronn@gmail.com"
                className="font-body text-white/80 hover:text-gold transition-colors duration-200"
                data-testid="footer-email"
              >
                ivoryandsaffronn@gmail.com
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-body text-sm font-semibold uppercase tracking-widest text-white mb-6">
              Quick Links
            </h4>
            <nav className="space-y-3">
              {footerLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="block font-body text-white/60 hover:text-gold transition-colors duration-200"
                  data-testid={`footer-link-${link.href.replace("#", "")}`}
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </div>

          {/* Trust Statement */}
          <div>
            <h4 className="font-body text-sm font-semibold uppercase tracking-widest text-white mb-6">
              Our Promise
            </h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-gold rounded-full mt-2 flex-shrink-0" />
                <p className="font-body text-white/60 text-sm">
                  100% Data Sovereignty—Your files stay on YOUR servers
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-gold rounded-full mt-2 flex-shrink-0" />
                <p className="font-body text-white/60 text-sm">
                  Zero Learning Curve—No new software to master
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-gold rounded-full mt-2 flex-shrink-0" />
                <p className="font-body text-white/60 text-sm">
                  Client-Friendly—If they can WhatsApp, they can use this
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-body text-sm text-white/40">
            © {new Date().getFullYear()} InwardSync Automations. All rights reserved.
          </p>
          <button
            onClick={scrollToTop}
            className="flex items-center gap-2 font-body text-sm text-white/40 hover:text-gold transition-colors duration-200"
            data-testid="scroll-to-top-button"
          >
            Back to top
            <ArrowUp size={16} />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
